<!--
  DestructiveConfirmDialog — reusable confirmation modal for destructive
  actions in the product editor.

  Pebble 11 of "Product editor (hi-fi redesign)"
  https://trello.com/c/Qlwx8mlT

  Thin v-dialog wrapper that lets a host (e.g. SupplierSkuPanel's SKU
  swap flow) prompt the user before discarding work. Built generic so
  the same dialog can host future destructive flows (decoration delete,
  campaign reset, etc.) — only the host's props change.

  Contract:
    - v-model boolean drives visibility
    - Confirm click → emits 'confirm' + sets v-model false
    - Cancel click → emits 'cancel' + sets v-model false
    - Scrim click / Esc (v-dialog emits update:modelValue=false)
      → emits 'cancel' (so hosts can revert pending state)
    - Exactly ONE of 'confirm' / 'cancel' is emitted per interaction.
      Vuetify echoes our own close back as update:model-value=false, which
      must NOT be re-read as a dismissal -- see closingFromAction below.
-->
<template>
    <v-dialog
        :model-value="modelValue"
        max-width="480"
        data-testid="destructive-confirm-dialog"
        @update:model-value="onUpdate"
    >
        <v-card>
            <v-card-title data-testid="destructive-confirm-title">
                {{ title }}
            </v-card-title>
            <v-card-text>
                <p data-testid="destructive-confirm-description">
                    {{ description }}
                </p>
                <ul
                    v-if="consequences.length"
                    data-testid="destructive-confirm-consequences"
                >
                    <li
                        v-for="(item, i) in consequences"
                        :key="i"
                        :data-testid="`destructive-confirm-consequence-${i}`"
                    >
                        {{ item }}
                    </li>
                </ul>
                <!-- Optional host content, e.g. how to fix what is warned about. -->
                <slot />
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    data-testid="destructive-confirm-cancel"
                    @click="onCancel"
                >
                    {{ cancelLabel }}
                </v-btn>
                <v-btn
                    :color="confirmColor"
                    :disabled="confirmDisabled"
                    data-testid="destructive-confirm-confirm"
                    @click="onConfirm"
                >
                    {{ confirmLabel }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true,
    },
    title: {
        type: String,
        required: true,
    },
    description: {
        type: String,
        default: '',
    },
    consequences: {
        type: Array,
        default: () => [],
    },
    confirmLabel: {
        type: String,
        default: 'Continue',
    },
    cancelLabel: {
        type: String,
        default: 'Cancel',
    },
    confirmColor: {
        type: String,
        default: 'warning',
    },
    // Hold Confirm until the host's own content is complete (card #545: the
    // Change SKU dialog's placement decisions).
    confirmDisabled: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

// `props` is read in handlers below to keep the script-setup compiler
// happy about the implicit usage (avoids "props declared but never used"
// flags from some linters).
void props

// A close we initiated ourselves (Confirm or Cancel was clicked). Vuetify's
// v-dialog echoes our `model-value` going false back as its own
// `update:model-value=false`, which is indistinguishable at `onUpdate` from a
// scrim click or Esc. Without this flag that echo was re-interpreted as a
// dismissal and emitted a SPURIOUS `cancel` after every `confirm`.
//
// The consequence was not cosmetic: ProductEditor's discard-abandoned-stub
// guard settles its route-leave promise on whichever event arrives LAST, so
// the trailing cancel resolved it as "stay" -- the abandoned row was never
// deleted and the navigation never completed, while the dialog closed and
// looked like it had worked.
let closingFromAction = false

function onUpdate(v) {
    emit('update:modelValue', v)
    if (v) return
    // Our own close echoing back -- already emitted confirm/cancel below.
    if (closingFromAction) {
        closingFromAction = false
        return
    }
    // A close that did NOT originate here is a scrim click or Esc; that is a
    // real dismissal and still means cancel.
    emit('cancel')
}

function onConfirm() {
    if (props.confirmDisabled) return
    closingFromAction = true
    emit('update:modelValue', false)
    emit('confirm')
}

function onCancel() {
    closingFromAction = true
    emit('update:modelValue', false)
    emit('cancel')
}
</script>
